import * as Yup from 'yup';
import { isArray, isEmpty } from 'lodash';
import { useSnackbar } from 'notistack';
import { camelCase } from 'change-case';
import InputMask from 'react-input-mask';
import { Add } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { usePatient, useValueSet } from 'src/careflow/ph-core/fhir-client/fhir-react';
import {
  ValueSetWrapper,
  WrappedValueSet,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import { FormProvider, RHFSelect, RHFTextField } from 'src/components/hook-form';
import {
  PatientWrapper,
  WrappedPatient,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import {
  Autocomplete,
  Button,
  Card,
  Chip,
  Dialog,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  ListItem,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ContactPoint,
  ContactPointUse,
  PatientContact,
} from 'src/careflow/ph-core/fhir-client/resources';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { convertValueToValueSetAll } from 'src/careflow/common/common-utils';
import AddAddressLoading from 'src/careflow/care-coordination/referral/components/workflow-step/AddressChecklistItem/AddAddressLoading';
import produce from 'immer';
import uuidv4 from 'src/utils/uuidv4';
import { capitalize } from 'src/utils/string';
import { getExtensionArrayByURLs } from 'src/utils/fhir';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import CancelButton from 'src/careflow/common/CancelButton';
import DialogCloseButton, { useUnsavedCloseGuard } from 'src/components/DialogCloseButton';

type FormValue = {
  id: string;
  firstName: string;
  lastName: string;
  middleName: string;
  gender: string;
  emailUse: string;
  email: string;
  phoneUse: string;
  street: string;
  street2: string;
  city: string;
  district: string;
  phone: string;
  state: string;
  relationshipCode: any[];
  zipCode: string;
  housingStatus: string;
};

type EditModeProps = {
  patient: WrappedPatient | null;
  contact: PatientContact | null;
  open: boolean;
  onClose: VoidFunction;
  relationshipValueSet: WrappedValueSet | null;
};

export default function ContactAction({
  patient,
  contact,
  open,
  onClose,
  relationshipValueSet,
}: EditModeProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { componentsData } = useTenantConfigData();
  const { relatedPerson: relatedPersonConfig } = componentsData;

  const [openAddAddress, setOpenAddAddress] = useState(false);
  const [addressDirty, setAddressDirty] = useState(false);
  const [address, setAddress] = useState<any>(null);
  const [externalError, setExternalError] = useState(false);

  const [genderCategories] = useValueSet('us-core-birthsex', { map: ValueSetWrapper });
  const [housingStatusVS] = useValueSet('udsplus-housing-status-codes', { map: ValueSetWrapper });

  const [, { update }] = usePatient(patient!.id!, {
    map: PatientWrapper,
  });
  const { i18n } = useLocales();

  const isAllowedAddressHousingStatus = checkAclValidation({
    acls: [crsAcls.CRS.PATIENT.RELATED_PERSONS.ADD_HOUSING_STATUS],
  });

  const EventSchema = Yup.object().shape({
    firstName: Yup.string()
      .required('First Name is required')
      .matches(/^[a-zA-Z\s]+$/, 'First Name cannot contain numbers'),
    lastName: Yup.string()
      .required('Last Name is required')
      .matches(/^[a-zA-Z\s-]+$/, 'Last Name cannot contain numbers'),
    email: Yup.string().email('Email must be a valid email address'),
    phone: Yup.string()
      .nullable()
      .notRequired()
      .test('test-phone', 'Please enter a valid phone number', function (value) {
        if (!value) return true;
        const isValidPhone = /^\([0-9]{3}\) [0-9]{3}-[0-9]{4}$/.test(value);
        if (!isValidPhone) {
          setValue?.('phoneUse', '');
        }
        return isValidPhone;
      }),
    phoneUse: Yup.string()
      .nullable()
      .notRequired()
      .when('phone', {
        is: (phone) => /^\([0-9]{3}\) [0-9]{3}-[0-9]{4}$/.test(phone || ''),
        then: Yup.string().required('Phone Use is required'),
      }),
    housingStatus: Yup.string()
      .nullable()
      .when([], {
        is: () =>
          relatedPersonConfig?.address?.requiredHousingStatus && isAllowedAddressHousingStatus,
        then: (schema) => schema.required('Housing Status is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
  });

  useEffect(() => {
    if (open) reset(defaultValues);
    setExternalError(false);
  }, [open]);

  useEffect(() => {
    reset(defaultValues);
    setExternalError(false);
  }, [contact]);

  const defaultValues = useMemo(
    () =>
      ({
        id: contact?.id || '',
        lastName: contact?.name?.family || '',
        firstName: contact?.name?.given?.[0] || '',
        middleName: contact?.name?.given?.[1] || '',
        gender: contact?.gender || '',
        emailUse: contact?.telecom?.filter((e) => e.system === 'email')?.[0].use || '',
        email: contact?.telecom?.filter((e) => e.system === 'email')?.[0].value || '',
        phoneUse: contact?.telecom?.filter((e) => e.system === 'phone')?.[0].use || '',
        phone: contact?.telecom?.filter((e) => e.system === 'phone')?.[0].value || '',
        street: contact?.address?.line?.[0] || '',
        street2: contact?.address?.line?.[1] || '',
        city: contact?.address?.city || '',
        district: contact?.address?.district || '',
        relationshipCode: contact?.relationship?.map((e) => e?.coding?.[0]) ?? [],
        state: contact?.address?.state || '',
        zipCode: contact?.address?.postalCode || '',
        housingStatus:
          getExtensionArrayByURLs(contact?.address, [
            'http://fhir.org/guides/hrsa/uds-plus/CodeSystem/udsplus-housing-status-codes',
          ])?.[0]?.valueCode ??
          relatedPersonConfig?.address?.defaultHousingStatus ??
          '',
      } as FormValue),
    [contact]
  );

  const methods = useForm({ resolver: yupResolver(EventSchema), defaultValues });

  const { reset, control, handleSubmit, setValue, watch } = methods;

  const requestClose = useUnsavedCloseGuard(onClose, methods.formState.isDirty);

  const values = watch();

  const saveChanges = async (data: any) => {
    setValue('street', data.line[0]);
    setValue('street2', data.line[1]);
    setValue('city', data.city);
    setValue('district', data.district);
    setValue('zipCode', data.postalCode);
    setValue('state', data.state);
    setAddress(data);
    setOpenAddAddress(false);
    setExternalError(false);
  };

  const onSubmit = async (data: FormValue) => {
    try {
      const telecom: ContactPoint[] = [];

      if (!isEmpty(data.email))
        telecom.push({
          rank: 1,
          system: 'email',
          use: data.emailUse as ContactPointUse,
          value: data.email,
        });
      if (!isEmpty(data.phone)) {
        telecom.push({
          rank: 1,
          system: 'phone',
          use: data.phoneUse as ContactPointUse,
          value: data.phone,
        });
      }

      const gender: any = data?.gender;

      const getContact = patient?.contact?.find((c: any) => c.id === data?.id);
      let getAddress: any;

      if (!isEmpty(address)) {
        if (isEmpty(address?.extension)) {
          address.extension = [];
        }

        if (values.housingStatus) {
          const housingStatusExtensionIndex = getContact?.address?.extension?.findIndex(
            (item) => item?.url === housingStatusVS?.getConceptSystem()
          );
          const housingStatusExtension = {
            url: housingStatusVS?.getConceptSystem(),
            valueCode: values.housingStatus,
          };
          if (housingStatusExtensionIndex !== undefined && housingStatusExtensionIndex !== -1) {
            address.extension[housingStatusExtensionIndex] = housingStatusExtension;
          } else {
            address.extension.push(housingStatusExtension);
          }
        }
        getAddress = address;
      } else {
        getAddress = getContact?.address;
      }

      const relationship = data?.relationshipCode?.map((e) => ({
        coding: [
          {
            ...convertValueToValueSetAll(e?.code, relationshipValueSet),
          },
        ],
        text: convertValueToValueSetAll(e?.code, relationshipValueSet)?.display,
      }));

      await update(
        produce(patient!, (draft) => {
          draft.contact = [
            ...(draft.contact?.filter((c: any) => c.id !== data?.id) || []),
            {
              id: data?.id || uuidv4(),
              name: {
                family: data?.lastName,
                given: [data.firstName, ...(!isEmpty(data.middleName) ? [data.middleName] : [])],
              },
              ...(!isEmpty(telecom) && { telecom: telecom }),
              ...(!isEmpty(getAddress) && { address: getAddress }),
              ...(!isEmpty(gender) && { gender: gender }),
              ...(relationship?.length > 0 ? { relationship: relationship } : null),
            },
          ];
        })
      );

      setAddress({});

      onClose();
      enqueueSnackbar(
        data?.id
          ? `${i18n('patients.details.contacts.msgUpdate', 'crs')}`
          : `${i18n('patients.details.contacts.msgCreate', 'crs')}`
      );
    } catch {
      enqueueSnackbar('An error has occurred', { variant: 'error' });
      onClose();
    }
  };

  const handlerClose = () => {
    setOpenAddAddress(false);
    setExternalError(false);
  };

  const requestCloseAddress = useUnsavedCloseGuard(() => handlerClose(), addressDirty);

  if (!open) return null;

  return (
    <>
      <Dialog onClose={() => void requestClose()} open={open} fullWidth={true} maxWidth="md">
        <DialogCloseButton onClose={onClose} hasUnsavedChanges={methods.formState.isDirty} />
        <DialogTitle>{i18n('patients.details.contacts.titleAddContact', 'crs')}</DialogTitle>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3} sx={{ p: 2 }}>
            <Grid item xs={12}>
              <Card sx={{ p: 2 }}>
                <Typography
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                  variant="button"
                  sx={{ textTransform: 'uppercase' }}
                >
                  {i18n('patients.details.contacts.personalDetails', 'crs')}
                </Typography>
                <Grid container spacing={2} sx={{ pt: 1 }}>
                  <Grid item xs={4}>
                    <RHFTextField
                      name="firstName"
                      label={`${i18n('patients.firstName', 'crs')}*`}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <RHFTextField name="middleName" label={i18n('patients.middleName', 'crs')} />
                  </Grid>
                  <Grid item xs={4}>
                    <RHFTextField name="lastName" label={`${i18n('patients.lastName', 'crs')}*`} />
                  </Grid>
                  <Grid item xs={4}>
                    <RHFSelect
                      name="gender"
                      label={i18n('patients.details.contacts.gender', 'crs')}
                      fullWidth={true}
                    >
                      <MenuItem disabled></MenuItem>
                      {genderCategories?.asList().map((option) => (
                        <MenuItem key={option.code} value={camelCase(option.display || '')}>
                          {option.display}
                        </MenuItem>
                      ))}
                    </RHFSelect>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card sx={{ p: 2 }}>
                <Typography
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                  variant="button"
                  sx={{ textTransform: 'uppercase' }}
                >
                  {i18n('patients.details.contacts.contactDetails', 'crs')}
                </Typography>
                <Grid container spacing={2} sx={{ pt: 1 }}>
                  <Grid item xs={12}>
                    {relationshipValueSet && (
                      <Grid item xs={12}>
                        <Controller
                          name="relationshipCode"
                          control={control}
                          render={({ field }) => (
                            <Autocomplete
                              {...field}
                              multiple
                              freeSolo
                              onChange={(event, newValue) => field.onChange(newValue)}
                              options={relationshipValueSet?.asListAll()}
                              getOptionLabel={(option) => option?.display}
                              isOptionEqualToValue={(option, value) => option.code === value.code}
                              filterOptions={(options, params) =>
                                options?.filter((item) =>
                                  item?.display
                                    ?.toLowerCase()
                                    .includes(params?.inputValue?.toLowerCase())
                                )
                              }
                              renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                  <Chip
                                    {...getTagProps({ index })}
                                    key={option?.code}
                                    size="small"
                                    label={option?.display}
                                  />
                                ))
                              }
                              renderInput={(params) => (
                                <TextField
                                  label={i18n('patients.details.contacts.relationship', 'crs')}
                                  {...params}
                                />
                              )}
                            />
                          )}
                        />
                      </Grid>
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    <RHFSelect
                      name="emailUse"
                      label={i18n('patients.details.contacts.emailUse', 'crs')}
                      fullWidth
                    >
                      <MenuItem value="home">Home</MenuItem>
                      <MenuItem value="work">Work</MenuItem>
                    </RHFSelect>
                  </Grid>
                  <Grid item xs={8}>
                    <RHFTextField name="email" label={i18n('patients.email', 'crs')} fullWidth />
                  </Grid>
                  <Grid item xs={4}>
                    <RHFSelect
                      name="phoneUse"
                      label={i18n('patients.details.contacts.phoneUse', 'crs')}
                      fullWidth
                    >
                      <MenuItem value="home">Home</MenuItem>
                      <MenuItem value="work">Work</MenuItem>
                      <MenuItem value="mobile">Mobile</MenuItem>
                    </RHFSelect>
                  </Grid>
                  <Grid item xs={8}>
                    <Controller
                      name="phone"
                      control={control}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <InputMask mask="(999) 999-9999" value={value} onChange={onChange}>
                          {(inputProps: any) => (
                            <TextField
                              label={i18n('patients.phone', 'crs')}
                              {...inputProps}
                              fullWidth
                              error={!!error}
                              helperText={error?.message}
                            />
                          )}
                        </InputMask>
                      )}
                    />
                  </Grid>
                </Grid>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card sx={{ p: 2 }}>
                <Typography
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                  variant="button"
                  sx={{ textTransform: 'uppercase' }}
                >
                  {i18n('patients.details.contacts.addressDetails', 'crs')}
                  <Button
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => setOpenAddAddress(true)}
                  >
                    Add
                  </Button>
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <RHFTextField
                      name="street"
                      label={i18n('patients.details.contacts.street', 'crs')}
                      fullWidth
                      disabled
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <RHFTextField
                      name="street2"
                      label={i18n('patients.details.contacts.streetTwo', 'crs')}
                      fullWidth
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={2}>
                      <RHFTextField
                        name="city"
                        label={i18n('patients.details.contacts.city', 'crs')}
                        fullWidth
                        disabled
                      />
                      <RHFTextField
                        name="district"
                        label={i18n('patients.details.contacts.country', 'crs')}
                        fullWidth
                        disabled
                      />
                      <RHFTextField
                        name="state"
                        label={i18n('patients.details.contacts.state', 'crs')}
                        fullWidth
                        disabled
                      />
                      <RHFTextField
                        name="zipCode"
                        label={i18n('patients.details.contacts.zipCode', 'crs')}
                        fullWidth
                        disabled
                      />
                      <RHFTextField
                        name="housingStatus"
                        label={i18n('patients.details.relatedPersons.housingStatus', 'crs')}
                        value={
                          values.housingStatus
                            ? capitalize(values?.housingStatus?.replaceAll('-', ' '))
                            : ''
                        }
                        fullWidth
                        disabled
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
          <Stack direction="row" spacing={2} justifyContent="center" sx={{ pb: 2 }}>
            <CancelButton onClick={() => void requestClose()}>{i18n('cancel')}</CancelButton>
            <Button variant="contained" type="submit">
              {i18n('saveChanges')}
            </Button>
          </Stack>
        </FormProvider>
      </Dialog>
      <Dialog onClose={() => void requestCloseAddress()} open={openAddAddress} fullWidth maxWidth="md">
        <DialogCloseButton onClose={handlerClose} hasUnsavedChanges={addressDirty} />
        <DialogTitle> Add Address</DialogTitle>
        <Card sx={{ p: 2, mt: 2 }}>
          <AddAddressLoading
            isEditable
            handleClose={handlerClose}
            handleSave={saveChanges}
            externalError={externalError}
            onDirtyChange={setAddressDirty}
            {...(isAllowedAddressHousingStatus
              ? {
                  childrenTop: (
                    <ListItem>
                      <FormControl fullWidth error={externalError}>
                        <InputLabel id="address-housing-status-label">
                          Housing Status{' '}
                          {relatedPersonConfig?.address?.requiredHousingStatus ? '*' : ''}
                        </InputLabel>
                        <Select
                          labelId="address-housing-status-label"
                          id="addressHousingStatus"
                          name="addressHousingStatus"
                          label={`Housing Status ${
                            relatedPersonConfig?.address?.requiredHousingStatus ? '*' : ''
                          }`}
                          value={values.housingStatus ?? ''}
                          onChange={(ev) => {
                            setValue('housingStatus', String(ev.target.value));
                            setExternalError(false);
                          }}
                        >
                          {housingStatusVS?.asList().map((option) => (
                            <MenuItem key={option.code} value={option.code}>
                              {capitalize(option?.code?.replaceAll('-', ' '))}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </ListItem>
                  ),
                }
              : {})}
          />
        </Card>
      </Dialog>
    </>
  );
}
